
/**
 * Module dependencies.
 */

import { ProjectProps } from 'src/types/projects';
import { assetUrlResolve } from 'src/core/utils/url-resolver';
import { color, units } from 'src/styles/utils';
import { kioskRoutes } from 'src/core/routes';
import { routeTemplate, useLocalizedRoute } from 'src/core/utils/routes';
import { switchProp, theme } from 'styled-tools';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import CardTitle from 'src/components/core/typography/card-title';
import Image, { ImageProps } from 'src/components/core/image';
import Label from 'src/components/core/typography/label';
import React, { Fragment, ReactElement, useCallback } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import styled, { css } from 'styled-components';

/**
 * `BgColorTheme` type.
 */

type BgColorTheme = 'grey' | 'white';

/**
 * `Props` type.
 */

type Props = ProjectProps & {
  bgColorTheme?: BgColorTheme,
  isKioskCard?: boolean
};

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)<ImageProps>`
  transition: transform 5s ease-in;
`;

/**
 * `Card` styled component.
 */

const Card = styled(RouterLink)<{ bgColorTheme: BgColorTheme }>`
  background-color: ${color('grey100')};
  color: ${color('textColor')};
  display: grid;
  grid-template-areas:
    'image image image'
    '.     title .'
    '.     info  .';
  grid-template-columns: ${units(3)} 1fr ${units(3)};
  grid-template-rows: ${units(36)} 1fr max-content;
  height: ${units(64)};
  padding-bottom: ${units(6.5)};
  transition: color ${theme('animations.defaultTransition')};

  ${switchProp('bgColorTheme', {
    grey: css`
      background-color: ${color('grey100')};
    `,
    white: css`
      background-color: ${color('white')};
    `
  })}

  &:focus,
  &:hover {
    color: ${color('brown400')};

    ${StyledImage} {
      transform: scale(1.5);
    }
  }
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  height: ${units(36)};
  overflow: hidden;
  position: relative;
`;

/**
 * `Title` styled component.
 */

const Title = styled(CardTitle).attrs({ size: 'small' })`
  grid-area: title;
  margin-bottom: ${units(3)};
  padding-top: ${units(3)};
`;

/**
 * `InfoWrapper` styled component.
 */

const InfoWrapper = styled.div`
  grid-area: info;
`;

/**
 * `StyledLabel` styled component.
 */

const StyledLabel = styled(Label).attrs({ size: 'small' })`
  color: ${color('grey900')};
  text-transform: uppercase;

  &:not(:last-child) {
    margin-bottom: ${units(1)};
  }
`;

/**
 * `CollectionsWrapper` styled component.
 */

const CollectionsWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
`;

/**
 * `CollectionLink` styled component.
 */

const CollectionLink = styled.span`
  border-bottom: 1px solid ${color('grey900')};
  color: ${color('grey900')};
  cursor: pointer;
  display: table;
  margin: 2px 0;
  text-decoration: none;
  text-transform: uppercase;
  transition: ${theme('animations.defaultTransition')};
  transition-property: border-bottom-color, color;

  &:focus,
  &:hover {
    border-bottom-color: ${color.transparentize('grey900', 0.3)};
    color: ${color.transparentize('grey900', 0.3)};
  }
`;

/**
 * `Separator` styled component.
 */

const Separator = styled(Label).attrs({ size: 'small' })`
  color: ${color('grey900')};
  margin: 0 ${units(1)};
`;

/**
 * `ProjectCard` component.
 */

const ProjectCard = (props: Props): ReactElement => {
  const router = useRouter();
  const { locale, translate } = useTranslate();
  const {
    bgColorTheme,
    category,
    collections,
    country,
    isKioskCard,
    media,
    name,
    slug
  } = props;

  const routeResolver = useLocalizedRoute();
  const handleCollectionClick = useCallback((event, slug) => {
    event.preventDefault();
    const href = isKioskCard
      ? routeTemplate(kioskRoutes.collection, { slug })
      : routeResolver('collection', { slug });

    router.push(href, undefined, { locale });
  }, [isKioskCard, locale, routeResolver, router]);

  const alt = get(head(media), 'customProperties.title');

  return (
    <Card
      bgColorTheme={bgColorTheme}
      href={isKioskCard
        ? routeTemplate(kioskRoutes.portfolioDetails, { slug })
        : routeResolver('portfolioDetails', { slug })}
      locale={locale}
    >
      <ImageWrapper>
        <StyledImage
          alt={alt ? alt : name}
          layout={'fill'}
          objectFit={'cover'}
          src={media[0]?.fileUrl ?? assetUrlResolve('/static/images/empty/list@2x.jpg')}
        />
      </ImageWrapper>

      <Title>
        {name}
      </Title>

      <InfoWrapper>
        <StyledLabel>
          {translate(`projects:categories.${camelCase(category)}`)}
        </StyledLabel>

        <StyledLabel>
          {country.name}
        </StyledLabel>

        {!isEmpty(collections) && (
          <CollectionsWrapper>
            {map(collections, (collection, index: number) => (
              <Fragment key={index}>
                <CollectionLink
                  onClick={event => handleCollectionClick(event, collection.slug)}
                >
                  <StyledLabel>
                    {collection.name}
                  </StyledLabel>
                </CollectionLink>

                {index < size(collections) - 1 && (
                  <Separator>
                    {'|'}
                  </Separator>
                )}
              </Fragment>
            ))}
          </CollectionsWrapper>
        )}
      </InfoWrapper>
    </Card>
  );
};

/**
 * Default props.
 */

ProjectCard.defaultProps = {
  bgColorTheme: 'grey100'
};

/**
 * Export `ProjectCard` component.
 */

export default ProjectCard;
